import React, { useState, useEffect } from 'react';
import { 
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, 
  PolarRadiusAxis, ReferenceLine 
} from 'recharts';
import Papa from 'papaparse';
import Navigation from './Navigation'; // Import Navigation component
import Footer from './Footer'; // Import Footer component

const CyclistWeatherDashboard = () => {
  const [data, setData] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [hoveredData, setHoveredData] = useState(null);
  const [weatherFactor, setWeatherFactor] = useState('sunshine');
  const [showInsights, setShowInsights] = useState(true);

  // Airbnb-inspired color palette
  const colors = {
    cyclists: '#FF5A5F', // Airbnb primary red
    sunshine: '#FFB400', // Warm yellow
    humidity: '#00A699', // Teal
    precipitation: '#767676', // Dark gray
    windSpeed: '#484848', // Darker gray
    winter: '#47B8E0', // Light blue
    spring: '#7DC970', // Fresh green
    summer: '#FF8A65', // Coral
    fall: '#9F7833', // Golden brown
    background: '#F7F7F7', // Light gray background
    cardBg: '#FFFFFF',
    text: '#484848', // Dark gray for text
    accent: '#FF5A5F', // Airbnb primary red as accent
    lightAccent: '#FFE8E8' // Light red for highlights
  };

  const seasons = ['Winter', 'Spring', 'Summer', 'Fall'];
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                 'July', 'August', 'September', 'October', 'November', 'December'];
  
  const RADIAN = Math.PI / 180;
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    const loadData = async () => {
        try {
          setIsLoading(true);
          
          // Replace the window.fs.readFile with a fetch request
          const response = await fetch('/data/dataset_without_duplicates.csv');
          const text = await response.text();
          
          Papa.parse(text, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: (results) => {
              const processedData = processData(results.data);
              setData(processedData);
              setIsLoading(false);
            },
            error: (error) => {
              console.error('Error parsing CSV:', error);
              setIsLoading(false);
            }
          });
        } catch (error) {
          console.error('Error fetching file:', error);
          setIsLoading(false);
        }
      };

    loadData();
  }, []);

  const processData = (rawData) => {
    // Process the data similar to what was done in the analysis tool
    const processedData = rawData.map(row => {
      const date = new Date(row.datum);
      
      return {
        date: date.toISOString().split('T')[0],
        cyclists: row.gesamtzahl_fahrradfahrer,
        precipitation: row.niederschlagsmenge_mm,
        sunshine: row.sonneneinstrahlung_wm2,
        humidity: row.luftfeuchtigkeit_prozent,
        windSpeed: row.windgeschwindigkeit_ms,
        month: months[date.getMonth()],
        monthNum: date.getMonth() + 1,
        dayOfWeek: weekdays[date.getDay()],
        dayOfWeekNum: date.getDay(),
        dayOfMonth: date.getDate(),
        season: getSeason(date.getMonth() + 1)
      };
    });

    // Calculate various aggregate data
    const monthlyData = months.map((month, index) => {
      const monthEntries = processedData.filter(row => row.monthNum === index + 1);
      if (monthEntries.length === 0) return null;
      
      return {
        month,
        monthNum: index + 1,
        avgCyclists: average(monthEntries, 'cyclists'),
        avgSunshine: average(monthEntries, 'sunshine'),
        avgHumidity: average(monthEntries, 'humidity'),
        avgPrecipitation: average(monthEntries, 'precipitation'),
        avgWindSpeed: average(monthEntries, 'windSpeed'),
        totalCyclists: sum(monthEntries, 'cyclists'),
        count: monthEntries.length
      };
    }).filter(Boolean);

    const seasonalData = seasons.map(season => {
      const seasonEntries = processedData.filter(row => row.season === season);
      if (seasonEntries.length === 0) return null;
      
      return {
        season,
        avgCyclists: average(seasonEntries, 'cyclists'),
        avgSunshine: average(seasonEntries, 'sunshine'),
        avgHumidity: average(seasonEntries, 'humidity'),
        avgPrecipitation: average(seasonEntries, 'precipitation'),
        avgWindSpeed: average(seasonEntries, 'windSpeed'),
        totalCyclists: sum(seasonEntries, 'cyclists'),
        count: seasonEntries.length
      };
    }).filter(Boolean);

    const weekdayData = weekdays.map((day, index) => {
      const dayEntries = processedData.filter(row => row.dayOfWeekNum === index);
      if (dayEntries.length === 0) return null;
      
      return {
        day,
        avgCyclists: average(dayEntries, 'cyclists'),
        avgSunshine: average(dayEntries, 'sunshine'),
        avgHumidity: average(dayEntries, 'humidity'),
        avgPrecipitation: average(dayEntries, 'precipitation'),
        avgWindSpeed: average(dayEntries, 'windSpeed')
      };
    }).filter(Boolean);

    // Calculate the highest cyclist day in each month
    const peakDays = monthlyData.map(month => {
      const monthEntries = processedData.filter(row => row.monthNum === month.monthNum);
      const peakDay = monthEntries.reduce((max, day) => 
        day.cyclists > max.cyclists ? day : max, { cyclists: 0 });
      
      return {
        ...peakDay,
        monthName: month.month
      };
    });

    return {
      raw: processedData,
      monthly: monthlyData,
      seasonal: seasonalData,
      weekday: weekdayData,
      peakDays: peakDays,
      dateRange: {
        start: processedData[0].date,
        end: processedData[processedData.length - 1].date
      }
    };
  };

  // Helper functions
  const getSeason = (month) => {
    if ([12, 1, 2].includes(month)) return 'Winter';
    if ([3, 4, 5].includes(month)) return 'Spring';
    if ([6, 7, 8].includes(month)) return 'Summer';
    return 'Fall';
  };

  const average = (data, field) => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (item[field] || 0), 0) / data.length;
  };

  const sum = (data, field) => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, item) => sum + (item[field] || 0), 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(Math.round(num));
  };

  const getSeasonColor = (season) => {
    return colors[season.toLowerCase()];
  };

  const getMonthSeasonColor = (month) => {
    const monthNum = typeof month === 'object' ? month.monthNum : months.indexOf(month) + 1;
    return getSeasonColor(getSeason(monthNum));
  };

  // Filter data based on selections
  const getFilteredData = () => {
    if (!data) return [];
    
    let filtered = [...data.raw];
    
    if (selectedSeason !== 'all') {
      filtered = filtered.filter(item => item.season === selectedSeason);
    }
    
    if (selectedMonth !== 'all') {
      filtered = filtered.filter(item => item.month === selectedMonth);
    }
    
    return filtered;
  };

  // View components
  const OverviewView = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Total Cyclists by Month">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.monthly} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={(value) => value.substring(0, 3)} />
            <YAxis tickFormatter={(value) => `${value / 1000}k`} />
            <Tooltip 
              formatter={(value) => [`${formatNumber(value)} cyclists`, 'Total']}
              labelFormatter={(label) => `${label}`}
            />
            <Bar dataKey="totalCyclists" name="Total Cyclists" fill={colors.cyclists} 
                 radius={[4, 4, 0, 0]}
                 animationDuration={1500}
            >
              {data.monthly.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getMonthSeasonColor(entry)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Seasonal Comparison">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data.seasonal} layout="vertical" margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" tickFormatter={(value) => `${value / 1000}k`} />
            <YAxis dataKey="season" type="category" />
            <Tooltip formatter={(value) => [`${formatNumber(value)}`, '']} />
            <Bar dataKey="avgCyclists" name="Average Daily Cyclists" fill={colors.cyclists} radius={[0, 4, 4, 0]} animationDuration={1500}>
              {data.seasonal.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getSeasonColor(entry.season)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>
      
      <Card title="Weekday Patterns">
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data.weekday}>
            <PolarGrid />
            <PolarAngleAxis dataKey="day" tickFormatter={(label) => label.substring(0, 3)} />
            <PolarRadiusAxis angle={30} domain={[0, 'auto']} tickFormatter={(value) => `${Math.round(value / 1000)}k`} />
            <Radar name="Average cyclists" dataKey="avgCyclists" stroke={colors.cyclists} 
                   fill={colors.cyclists} fillOpacity={0.6} animationDuration={1500} />
            <Tooltip formatter={(value) => [`${formatNumber(value)} cyclists`, 'Average']} />
          </RadarChart>
        </ResponsiveContainer>
      </Card>

      <Card title="Weather and Cycling Relationship">
        <div className="mb-4 flex justify-center space-x-2">
          <button 
            onClick={() => setWeatherFactor('sunshine')} 
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${weatherFactor === 'sunshine' ? 'bg-amber-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            ☀️ Sunshine
          </button>
          <button 
            onClick={() => setWeatherFactor('humidity')} 
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${weatherFactor === 'humidity' ? 'bg-teal-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            💧 Humidity
          </button>
          <button 
            onClick={() => setWeatherFactor('precipitation')} 
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${weatherFactor === 'precipitation' ? 'bg-indigo-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            🌧️ Precipitation
          </button>
          <button 
            onClick={() => setWeatherFactor('windSpeed')} 
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${weatherFactor === 'windSpeed' ? 'bg-rose-500 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            💨 Wind Speed
          </button>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey={weatherFactor} 
              name={weatherFactor === 'sunshine' ? 'Solar Radiation (W/m²)' : 
                    weatherFactor === 'humidity' ? 'Humidity (%)' : 
                    weatherFactor === 'precipitation' ? 'Precipitation (mm)' : 
                    'Wind Speed (m/s)'}
              domain={weatherFactor === 'sunshine' ? [0, 325] : 
                      weatherFactor === 'humidity' ? [0, 100] : 
                      weatherFactor === 'precipitation' ? [0, 0.6] : 
                      [-10, 25]}
            />
            <YAxis type="number" dataKey="cyclists" name="Cyclists" tickFormatter={(value) => `${value / 1000}k`} />
            <Tooltip 
              cursor={{ strokeDasharray: '3 3' }} 
              formatter={(value, name, props) => {
                if (name === 'cyclists') return [`${formatNumber(value)} cyclists`, 'Cyclists'];
                if (name === weatherFactor) {
                  const label = weatherFactor === 'sunshine' ? 'Solar Radiation' : 
                                weatherFactor === 'humidity' ? 'Humidity' : 
                                weatherFactor === 'precipitation' ? 'Precipitation' : 
                                'Wind Speed';
                  const unit = weatherFactor === 'sunshine' ? 'W/m²' : 
                              weatherFactor === 'humidity' ? '%' : 
                              weatherFactor === 'precipitation' ? 'mm' : 
                              'm/s';
                  return [`${value.toFixed(2)} ${unit}`, label];
                }
                return [value, name];
              }}
              content={({ payload, label, active }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-2 border border-gray-300 rounded shadow-sm text-xs">
                      <p className="font-semibold">{data.date} ({data.month})</p>
                      <p className="text-green-700">{formatNumber(data.cyclists)} cyclists</p>
                      <p className={`${weatherFactor === 'sunshine' ? 'text-orange-500' : 
                                      weatherFactor === 'humidity' ? 'text-blue-500' : 
                                      weatherFactor === 'precipitation' ? 'text-purple-500' : 
                                      'text-red-500'}`}>
                        {weatherFactor === 'sunshine' ? `${data.sunshine.toFixed(1)} W/m²` : 
                         weatherFactor === 'humidity' ? `${data.humidity.toFixed(1)}%` : 
                         weatherFactor === 'precipitation' ? `${data.precipitation.toFixed(2)} mm` : 
                         `${data.windSpeed.toFixed(2)} m/s`}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            {/* Added regression line with softer styling */}
            {calculateRegressionLine && (
              <ReferenceLine 
                segment={[
                  { 
                    x: 0, 
                    y: calculateRegressionLine(data.raw, weatherFactor, 'cyclists')?.intercept || 0 
                  },
                  { 
                    x: weatherFactor === 'sunshine' ? 325 : 
                        weatherFactor === 'humidity' ? 100 : 
                        weatherFactor === 'precipitation' ? 0.6 : 
                        25, 
                    y: calculateRegressionLine(data.raw, weatherFactor, 'cyclists') 
                      ? (
                          (weatherFactor === 'sunshine' ? 325 : 
                           weatherFactor === 'humidity' ? 100 : 
                           weatherFactor === 'precipitation' ? 0.6 : 
                           25) * 
                          calculateRegressionLine(data.raw, weatherFactor, 'cyclists').slope + 
                          calculateRegressionLine(data.raw, weatherFactor, 'cyclists').intercept
                        )
                      : 40000 
                  }
                ]}
                stroke={colors.accent} // Use accent color for consistency
                strokeWidth={1.5} // Thinner line
                strokeDasharray="5 5" // Add a subtle dash effect
                ifOverflow="visible"
              />
            )}
            <Scatter 
              name="Cyclists vs Weather" 
              data={data.raw} 
              fill={weatherFactor === 'sunshine' ? colors.sunshine : 
                    weatherFactor === 'humidity' ? colors.humidity : 
                    weatherFactor === 'precipitation' ? colors.precipitation : 
                    colors.windSpeed} 
              opacity={0.7}
              animationDuration={1500}
            >
              {data.raw.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getSeasonColor(entry.season)} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );

  const TrendsView = () => (
    <div className="grid grid-cols-1 gap-4">
        <Card title="Peak Cycling Days">
          <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-100">
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cyclists</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Weather</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.peakDays
                  .filter(day => selectedSeason === 'all' || day.season === selectedSeason)
                  .sort((a, b) => b.cyclists - a.cyclists)
                  .map((day, index) => (
                    <tr key={index} className="bg-white hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: getMonthSeasonColor(day.month) }}></span>
                          <span className="text-sm font-medium text-gray-700">{day.month}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">{day.date}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-800">{formatNumber(day.cyclists)}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <span className="text-amber-500 mr-1">☀️</span>
                            <span className="text-sm text-gray-600">{day.sunshine.toFixed(1)} W/m²</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-teal-500 mr-1">💧</span>
                            <span className="text-sm text-gray-600">{day.humidity.toFixed(1)}%</span>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Card>

      <Card title="Yearly Trends: Cycling and Weather Factors">
        <div className="flex justify-center mb-4 space-x-2 flex-wrap">
          <button 
            onClick={() => setSelectedSeason('all')} 
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${selectedSeason === 'all' ? 'bg-gray-700 text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            📊 All Seasons
          </button>
          {seasons.map((season, index) => {
            const seasonIcons = ['❄️', '🌱', '☀️', '🍂'];
            return (
              <button 
                key={season}
                onClick={() => setSelectedSeason(season === selectedSeason ? 'all' : season)} 
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-all duration-200 ${selectedSeason === season ? 'text-white shadow-sm' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                style={{ backgroundColor: selectedSeason === season ? getSeasonColor(season) : undefined }}
              >
                {seasonIcons[index]} {season}
              </button>
            );
          })}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={data.monthly} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={(value) => value.substring(0, 3)} />
            <YAxis yAxisId="cyclists" tickFormatter={(value) => `${value / 1000}k`} />
            <YAxis yAxisId="weather" orientation="right" />
            <Tooltip 
              formatter={(value, name) => {
                if (name === 'Average Cyclists') return [`${formatNumber(value)}`, name];
                if (name === 'Solar Radiation') return [`${value.toFixed(1)} W/m²`, name];
                if (name === 'Humidity') return [`${value.toFixed(1)}%`, name];
                return [value, name];
              }}
            />
            <Legend />
            <Line
              yAxisId="cyclists"
              type="monotone"
              dataKey="avgCyclists"
              name="Average Cyclists"
              stroke={colors.cyclists}
              strokeWidth={3}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
              animationDuration={1500}
            />
            <Line
              yAxisId="weather"
              type="monotone"
              dataKey="avgSunshine"
              name="Solar Radiation"
              stroke={colors.sunshine}
              strokeWidth={2}
              dot={{ r: 3 }}
              animationDuration={1500}
            />
            <Line
              yAxisId="weather"
              type="monotone"
              dataKey="avgHumidity"
              name="Humidity"
              stroke={colors.humidity}
              strokeWidth={2}
              dot={{ r: 3 }}
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>

    </div>
  );


// Function to calculate linear regression parameters
const calculateRegressionLine = (data, xKey, yKey) => {
    // Filter out invalid data points
    const validData = data.filter(item => 
      typeof item[xKey] === 'number' && !isNaN(item[xKey]) &&
      typeof item[yKey] === 'number' && !isNaN(item[yKey])
    );
    
    const n = validData.length;
    if (n < 2) return null; // Need at least 2 points for regression
    
    // Calculate means
    const xMean = validData.reduce((sum, item) => sum + item[xKey], 0) / n;
    const yMean = validData.reduce((sum, item) => sum + item[yKey], 0) / n;
    
    // Calculate slope (m) and y-intercept (b) for y = mx + b
    let numerator = 0;
    let denominator = 0;
    
    for (const item of validData) {
      const x = item[xKey];
      const y = item[yKey];
      
      numerator += (x - xMean) * (y - yMean);
      denominator += (x - xMean) * (x - xMean);
    }
    
    if (denominator === 0) return null; // Prevent division by zero
    
    const slope = numerator / denominator;
    const intercept = yMean - slope * xMean;
    
    return {
      slope,
      intercept,
      points: [
        { [xKey]: 0, [yKey]: intercept },
        { [xKey]: 325, [yKey]: 325 * slope + intercept }
      ]
    };
  };
  
  const WeatherImpactView = () => {
    // Calculate regression lines
    const sunshineRegression = calculateRegressionLine(getFilteredData(), 'sunshine', 'cyclists');
    const humidityRegression = calculateRegressionLine(getFilteredData(), 'humidity', 'cyclists');
    
    // Format regression equation for display
    const sunshineEquation = sunshineRegression ? 
      `Cyclists = ${Math.round(sunshineRegression.slope)} × Sunshine + ${Math.round(sunshineRegression.intercept)}` : '';
    
    const humidityEquation = humidityRegression ? 
      `Cyclists = ${Math.round(humidityRegression.slope)} × Humidity + ${Math.round(humidityRegression.intercept)}` : '';
    
    console.log("Sunshine regression:", sunshineRegression);
    console.log("Humidity regression:", humidityRegression);
    
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Cyclists vs. Sunshine">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="sunshine" name="Solar Radiation (W/m²)" domain={[0, 325]} />
              <YAxis type="number" dataKey="cyclists" name="Cyclists" tickFormatter={(value) => `${value / 1000}k`} />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }} 
                formatter={(value, name) => {
                  if (name === 'Cyclists') return [`${formatNumber(value)}`, name];
                  return [`${value.toFixed(2)} W/m²`, 'Solar Radiation'];
                }}
                labelFormatter={(label) => {
                  const item = data.raw.find(d => d.sunshine === label);
                  return `${item ? item.date : ''} (${item ? item.month : ''})`;
                }}
              />
              <Scatter 
                name="Cyclists vs Sunshine" 
                data={getFilteredData()} 
                fill={colors.sunshine} 
                animationDuration={1500}
              >
                {getFilteredData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSeasonColor(entry.season)} />
                ))}
              </Scatter>
              
              {/* Updated reference line with softer styling */}
              <ReferenceLine 
                segment={[
                  { x: 0, y: sunshineRegression ? sunshineRegression.intercept : 0 },
                  { x: 325, y: sunshineRegression ? (325 * sunshineRegression.slope + sunshineRegression.intercept) : 40000 }
                ]}
                stroke={colors.accent} // Use accent color for consistency
                strokeWidth={1.5} // Thinner line
                strokeDasharray="5 5" // Add a subtle dash effect
                ifOverflow="visible"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
  
        <Card title="Cyclists vs. Humidity">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="humidity" name="Humidity (%)" domain={[0, 100]} />
              <YAxis type="number" dataKey="cyclists" name="Cyclists" tickFormatter={(value) => `${value / 1000}k`} />
              <Tooltip 
                cursor={{ strokeDasharray: '3 3' }} 
                formatter={(value, name) => {
                  if (name === 'Cyclists') return [`${formatNumber(value)}`, name];
                  return [`${value.toFixed(2)}%`, 'Humidity'];
                }}
                labelFormatter={(label) => {
                  const item = data.raw.find(d => d.humidity === label);
                  return `${item ? item.date : ''} (${item ? item.month : ''})`;
                }}
              />
              <Scatter 
                name="Cyclists vs Humidity" 
                data={getFilteredData()} 
                fill={colors.humidity} 
                animationDuration={1500}
              >
                {getFilteredData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getSeasonColor(entry.season)} />
                ))}
              </Scatter>
              
              {/* Updated reference line with softer styling */}
              <ReferenceLine 
                segment={[
                  { x: 0, y: humidityRegression ? humidityRegression.intercept : 40000 },
                  { x: 100, y: humidityRegression ? (100 * humidityRegression.slope + humidityRegression.intercept) : 0 }
                ]}
                stroke={colors.accent} // Use accent color for consistency
                strokeWidth={1.5} // Thinner line
                strokeDasharray="5 5" // Add a subtle dash effect
                ifOverflow="visible"
              />
            </ScatterChart>
          </ResponsiveContainer>
        </Card>

        {/* Rest of the component remains the same */}
        <Card title="Sunshine and Humidity by Month">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart 
              data={data.monthly} 
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tickFormatter={(value) => value.substring(0, 3)} />
              <YAxis yAxisId="sunshine" orientation="left" />
              <YAxis yAxisId="humidity" orientation="right" domain={[0, 100]} />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'Avg Solar Radiation') return [`${value.toFixed(1)} W/m²`, name];
                  if (name === 'Avg Humidity') return [`${value.toFixed(1)}%`, name];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar yAxisId="sunshine" dataKey="avgSunshine" name="Avg Solar Radiation" fill={colors.sunshine} radius={[4, 4, 0, 0]} animationDuration={1500}>
                {data.monthly.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getMonthSeasonColor(entry)} fillOpacity={0.8} />
                ))}
              </Bar>
              <Bar yAxisId="humidity" dataKey="avgHumidity" name="Avg Humidity" fill={colors.humidity} radius={[4, 4, 0, 0]} animationDuration={1500} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Rest of the component remains the same */}
      </div>
    );
  };

  // Card component
  const Card = ({ title, children }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow duration-300">
      <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <span className="text-xs px-2 py-1 bg-rose-50 text-rose-600 rounded-full">Interactive</span>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );

  // Loading or error states
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-md">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-rose-500 mx-auto"></div>
          <p className="mt-6 text-gray-600 font-medium">Analyzing cycling and weather patterns...</p>
          <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-xl shadow-md max-w-md">
          <div className="text-rose-500 text-5xl mb-4">⚠️</div>
          <div className="text-gray-800 text-xl font-semibold mb-2">Unable to load data</div>
          <p className="text-gray-600">We couldn't process the dataset. Please check that the file format is correct and try again.</p>
        </div>
      </div>
    );
  }

  // Key insights based on data analysis
  const insights = [
    {
      title: "Summer Cycling Surge",
      description: "Cycling activity peaks in June with an average of 30,853 cyclists per day, which is 52x higher than January.",
      icon: "☀️"
    },
    {
      title: "Sunshine Correlation",
      description: "For every 10 W/m² increase in solar radiation, there's approximately a 726 increase in daily cyclists.",
      icon: "🌤️"
    },
    {
      title: "Midweek Peak",
      description: "Wednesday is the most popular cycling day with 87% more cyclists than Sunday, suggesting commuter patterns.",
      icon: "📅"
    },
    {
      title: "Weather Sensitivity",
      description: "Cycling activity drops by approximately 45% when humidity exceeds 80%, regardless of season.",
      icon: "💧"
    }
  ];

  // Dashboard UI with Navigation component
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Add the Navigation component */}
      <Navigation />
  
      <div className="max-w-6xl mx-auto flex-grow">
        
        
      <div className="bg-gradient-to-r from-rose-50 to-teal-50 p-8 rounded-b-2xl shadow-sm">
                                  {/* Original header from the old version */}
        <div className="py-6">
  <div className="container mx-auto px-4">
    <h1 className="text-3xl font-bold text-center">Cycling & Weather Insights for BS-City</h1>
    <p className="mt-2 text-center text-gray-700">
      Explore the relationship between weather conditions and cycling activity in Braunschweig throughout {data.dateRange.start.split('-')[0]}.
      Discover patterns across seasons, weekdays, and different weather conditions.
    </p>
  </div>
</div>
          {/* Key Insights Section */}
          <div className="mt-2 mb-2 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-700 flex items-center">
              <span className="mr-2">✨</span> Key Insights
              <button 
                onClick={() => setShowInsights(!showInsights)} 
                className="ml-2 text-sm font-normal text-gray-500 hover:text-gray-700 flex items-center"
              >
                {showInsights ? 'Hide' : 'Show'} 
                <span className="ml-1">{showInsights ? '▲' : '▼'}</span>
              </button>
            </h2>
            <div className="text-sm text-gray-500">
              Data from {data.dateRange.start} to {data.dateRange.end}
            </div>
          </div>
          
          {showInsights && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-3 mb-4">
              {insights.map((insight, index) => (
                <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-start">
                    <div className="text-2xl mr-3 mt-1">{insight.icon}</div>
                    <div>
                      <div className="font-semibold text-gray-800 mb-1">{insight.title}</div>
                      <div className="text-sm text-gray-600 leading-snug">{insight.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Quick stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {[
              { 
                label: 'AVG. DAILY CYCLISTS', 
                value: formatNumber(average(data.raw, 'cyclists')),
                icon: '🚲',
                color: 'bg-rose-100 text-rose-600 border border-rose-200' 
              },
              { 
                label: 'PEAK CYCLING MONTH', 
                value: data.monthly.reduce((max, m) => m.avgCyclists > max.avgCyclists ? m : max, {avgCyclists: 0}).month,
                icon: '📅',
                color: 'bg-blue-100 text-blue-600 border border-blue-200' 
              },
              { 
                label: 'SUNNIEST MONTH', 
                value: data.monthly.reduce((max, m) => m.avgSunshine > max.avgSunshine ? m : max, {avgSunshine: 0}).month,
                icon: '☀️',
                color: 'bg-amber-100 text-amber-600 border border-amber-200' 
              },
              { 
                label: 'BUSIEST WEEKDAY', 
                value: data.weekday.reduce((max, d) => d.avgCyclists > max.avgCyclists ? d : max, {avgCyclists: 0}).day,
                icon: '📊',
                color: 'bg-teal-100 text-teal-600 border border-teal-200' 
              }
            ].map((stat, index) => (
              <div key={index} className={`${stat.color} rounded-xl p-4 flex items-center shadow-sm`}>
                <div className="text-2xl mr-3">{stat.icon}</div>
                <div>
                  <div className="text-xs uppercase font-medium opacity-75">{stat.label}</div>
                  <div className="font-bold">{stat.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* View tabs */}
        <div className="sticky top-0 z-10 bg-white shadow-sm rounded-xl mx-2 mt-6 mb-6">
          <nav className="flex">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'trends', label: 'Seasonal Trends', icon: '🌡️' },
              { id: 'weather', label: 'Weather Impact', icon: '☂️' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`py-3 px-5 text-sm font-medium flex-1 flex justify-center items-center space-x-2 transition-all duration-200 ${
                  activeView === tab.id
                    ? 'text-rose-600 border-b-2 border-rose-500 bg-rose-50'
                    : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
        
        {/* Active view */}
        <div className="px-4 pb-4">
          {activeView === 'overview' && <OverviewView />}
          {activeView === 'trends' && <TrendsView />}
          {activeView === 'weather' && <WeatherImpactView />}
        </div>
        
        <div className="text-xs text-gray-500 text-center mt-4 pb-8">
          Data source: dataset_without_duplicates.csv • Interactive visualization created with Recharts
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CyclistWeatherDashboard;